/**
 * Shared, side-effect-free helper used by the reservation panel and the
 * proposal card so both surfaces describe a pending adjustment with the
 * same numbers and wording.
 *
 * The proposal payload always carries the *new* total (`payment_due`).
 * The current trip carries the total the renter has already agreed to.
 * The delta is what the renter will be charged or refunded right now if
 * the proposal is accepted as-is.
 *
 * Task #2409: every amount is rendered through `formatPriceCode` so
 * the booking's currency drives the symbol (and ambiguous-`$` ISO
 * suffix). There is intentionally no fallback / hard-coded symbol —
 * callers MUST resolve the trip's currency (e.g. via
 * `resolveTripCurrency(trip)`) and pass it in.
 */

import { formatPriceCode } from "./formatMoney";

export type AdjustmentDeltaKind = "charge" | "refund" | "none" | "unknown";

export interface AdjustmentDelta {
  kind: AdjustmentDeltaKind;
  /** Absolute difference in the booking's currency. 0 when kind is "none". */
  amount: number;
  /** The proposed new total (rounded to cents). */
  newTotal: number;
  /** The current total used as the baseline (rounded to cents). */
  currentTotal: number;
  /** Long-form sentence: "You'll be charged £42.00 more now". */
  headline: string;
  /** Short pill-friendly label: "+£42.00 due now". */
  badge: string;
}

function round2(n: number): number {
  return Math.round(Number(n) * 100) / 100;
}

/**
 * Who is reading this delta. Hosts never have money taken from them when
 * a trip is adjusted — the guest pays more or gets refunded out of the
 * escrowed amount. The renter ("guest") is the one whose card is moved.
 */
export type AdjustmentDeltaViewer = "host" | "renter";

/**
 * Task #3043 / #3047 — both host- and renter-side headlines are
 * status-aware so an already-accepted proposal doesn't keep reading in
 * future tense next to the green Accepted badge.
 *
 *   - "pending": host hasn't responded yet — keep the conditional /
 *     immediate-future wording ("...if accepted" for hosts,
 *     "You'll be charged …" for renters).
 *   - "accepted": host has accepted AND the trip has settled — read in
 *     past/settled tense (host: "You'll receive £X more"; renter:
 *     "You were charged £X more").
 *   - "accepted_pending_payment": host has accepted but the renter
 *     still owes 3DS / payment authorization — the amount is
 *     contingent on the renter's action. For hosts this reads "...once
 *     the renter authorizes payment"; for renters it reads as a call
 *     to action ("Complete payment to be charged £X more").
 *
 * Defaults to "pending" when omitted so existing call sites keep their
 * current copy.
 */
export type AdjustmentProposalStatus =
  | "pending"
  | "accepted"
  | "accepted_pending_payment";

export function computeAdjustmentDelta(
  currentTotal: number | null | undefined,
  newTotal: number | null | undefined,
  viewer: AdjustmentDeltaViewer,
  currency: string,
  proposalStatus: AdjustmentProposalStatus = "pending",
): AdjustmentDelta {
  if (typeof currency !== "string" || currency.trim() === "") {
    // Mirrors `formatMoneyWithCurrency`'s contract — refuse to silently
    // default to USD so a proposal banner can never display the wrong
    // symbol on a non-USD booking again (#2409).
    throw new Error("computeAdjustmentDelta: `currency` is required");
  }
  const fmt = (n: number) => formatPriceCode(n, currency);
  const current = round2(Number(currentTotal ?? 0));
  const target = round2(Number(newTotal ?? 0));
  const diff = round2(target - current);
  const isHost = viewer === "host";

  if (Math.abs(diff) < 0.01) {
    return {
      kind: "none",
      amount: 0,
      newTotal: target,
      currentTotal: current,
      headline: isHost ? "No change to your payout" : "No change to what you've paid",
      badge: "No payment change",
    };
  }

  // Task #3043 — host-side suffix flips with the proposal's effective
  // status.
  const hostSuffix =
    proposalStatus === "accepted"
      ? ""
      : proposalStatus === "accepted_pending_payment"
        ? " once the renter authorizes payment"
        : " if accepted";

  if (diff > 0) {
    // Task #3047 — renter charge wording is status-aware. By the time
    // the proposal is "accepted" the card has already been charged, so
    // future-tense reads oddly next to the green Accepted badge. In
    // "accepted_pending_payment" the host has accepted but the renter
    // still needs to complete 3DS, so the headline is a call to action.
    const renterChargeHeadline =
      proposalStatus === "accepted"
        ? `You were charged ${fmt(diff)} more`
        : proposalStatus === "accepted_pending_payment"
          ? `Complete payment to be charged ${fmt(diff)} more`
          : `You'll be charged ${fmt(diff)} more now`;
    return {
      kind: "charge",
      amount: diff,
      newTotal: target,
      currentTotal: current,
      headline: isHost
        ? `You'll receive ${fmt(diff)} more${hostSuffix}`
        : renterChargeHeadline,
      badge: isHost ? `+${fmt(diff)} to you` : `+${fmt(diff)} due now`,
    };
  }

  const refundAmt = -diff;
  // Task #3047 — renter refund wording is status-aware. Refunds aren't
  // gated by 3DS, so `accepted_pending_payment` (a charge-only state in
  // practice) collapses to the settled past-tense wording for safety
  // rather than carrying a misleading "Complete payment …" CTA.
  const renterRefundHeadline =
    proposalStatus === "accepted" || proposalStatus === "accepted_pending_payment"
      ? `You were refunded ${fmt(refundAmt)}`
      : `You'll be refunded ${fmt(refundAmt)}`;
  return {
    kind: "refund",
    amount: refundAmt,
    newTotal: target,
    currentTotal: current,
    headline: isHost
      ? `You'll receive ${fmt(refundAmt)} less${hostSuffix}`
      : renterRefundHeadline,
    badge: isHost ? `-${fmt(refundAmt)} to you` : `${fmt(refundAmt)} refund`,
  };
}

/**
 * Task #3531 — partial-pay-aware renter delta resolver.
 *
 * On partial-payment bookings the renter's card only ever moves the
 * platform portion of the trip (`platform_payment_due`); the remainder
 * (`owner_earn`) is paid directly to the owner at pickup and never
 * touches the card. The renter-facing "you'll be charged / +X vs
 * original" figures must therefore be computed from the platform
 * portion, NOT the full trip total (`payment_due`) — otherwise the
 * charged-now amount is overstated by the owner-direct slice.
 *
 * The platform portion is derived from values already present at every
 * call site via the exact money identity
 *   `payment_due === platform_payment_due + owner_earn`
 * (proven from the quote engine: full total = rental + service +
 * insurance + delivery − discounts, which equals the platform's cut
 * plus the host's `owner_earn`). So the card portion is simply
 * `total − owner_earn`, with no extra field plumbing required.
 *
 * Full-payment bookings are returned BYTE-FOR-BYTE identical to the
 * legacy `computeAdjustmentDelta(currentTotal, proposedTotal, "renter",
 * …)` path: `card` is the full-total delta, `ownerAtPickup` is null,
 * and the new/current totals are the full trip totals.
 *
 * Hosts are intentionally NOT routed through this helper — host
 * earnings deltas already read from `owner_earn` and stay on plain
 * `computeAdjustmentDelta`.
 */
export interface RenterAdjustmentDeltaInput {
  captureCurrency?: string | null;
  proposedCardTarget?: number | null;
  originalCardTarget?: number | null;
  hostDirectPaymentDue?: number | null;
  originalHostDirectPaymentDue?: number | null;
  /** Booking currency — same required contract as `computeAdjustmentDelta`. */
  currency: string;
  proposalStatus?: AdjustmentProposalStatus;
  /** True when the booking is a partial-payment ("pay platform now, owner at pickup") booking. */
  isPartial: boolean;
  /** Current (committed) full renter-facing trip total. */
  currentTotal: number | null | undefined;
  /** Proposed full renter-facing trip total. */
  proposedTotal: number | null | undefined;
  /** Current (committed) owner-direct (at-pickup) portion. Ignored when full-pay. */
  currentOwnerEarn?: number | null;
  /** Proposed owner-direct (at-pickup) portion. Ignored when full-pay. */
  proposedOwnerEarn?: number | null;
  /**
   * Optional explicit baseline for the amount already captured on the
   * renter's card (e.g. the server's `current_net_captured`, which is
   * the platform portion already charged on a partial booking). When
   * provided AND partial, the card delta is measured against this
   * captured amount instead of the derived committed platform basis
   * (`currentTotal − currentOwnerEarn`). Full-pay ignores it.
   */
  currentCardCapturedBasis?: number | null;
}

export interface RenterAdjustmentDelta {
  isPartial: boolean;
  /**
   * The charged-now (card) delta — drives the headline + badge. For
   * partial bookings this is the platform-portion delta; for full-pay
   * it is the full-total delta (identical to the legacy behaviour).
   */
  card: AdjustmentDelta;
  /**
   * The change in what the renter pays the owner directly at pickup.
   * `null` for full-pay bookings (nothing is paid outside the card).
   */
  ownerAtPickup: AdjustmentDelta | null;
  /** Full renter-facing proposed total (for the "New total" line). */
  newTotal: number;
  /** Full renter-facing current/committed total. */
  currentTotal: number;
}

export function computeRenterAdjustmentDelta(
  input: RenterAdjustmentDeltaInput,
): RenterAdjustmentDelta {
  const { currency, proposalStatus = "pending", isPartial } = input;
  const currentTotal = round2(Number(input.currentTotal ?? 0));
  const proposedTotal = round2(Number(input.proposedTotal ?? 0));
  const qualified = input.captureCurrency != null
    || input.proposedCardTarget !== undefined || input.originalCardTarget !== undefined;
  const cardCurrency = input.captureCurrency || currency;
  const unknownCard: AdjustmentDelta = {
    kind: "unknown", amount: 0, newTotal: proposedTotal, currentTotal,
    headline: "Payment change unavailable — please refresh before accepting",
    badge: "Payment change unavailable",
  };
  if (qualified) {
    const basis = proposalStatus.startsWith("accepted") ? input.originalCardTarget : input.currentCardCapturedBasis;
    const known = input.captureCurrency != null && basis != null && input.proposedCardTarget != null;
    const direct = input.hostDirectPaymentDue;
    const originalDirect = input.originalHostDirectPaymentDue;
    const card = known
      ? computeAdjustmentDelta(basis, input.proposedCardTarget, "renter", cardCurrency, proposalStatus)
      : unknownCard;
    if (card.kind === "none") card.headline = "No additional card charge";
    return {
      isPartial,
      card,
      ownerAtPickup: isPartial && direct != null && originalDirect != null
        ? computeAdjustmentDelta(originalDirect, direct, "renter", currency, proposalStatus) : null,
      newTotal: proposedTotal, currentTotal,
    };
  }

  if (!isPartial) {
    // Full-pay: identical to the legacy single-total renter delta.
    return {
      isPartial: false,
      card: computeAdjustmentDelta(currentTotal, proposedTotal, "renter", currency, proposalStatus),
      ownerAtPickup: null,
      newTotal: proposedTotal,
      currentTotal,
    };
  }

  if (input.currentOwnerEarn == null || input.proposedOwnerEarn == null) {
    return { isPartial: true, card: unknownCard, ownerAtPickup: null, newTotal: proposedTotal, currentTotal };
  }
  const currentOwnerEarn = round2(Number(input.currentOwnerEarn));
  const proposedOwnerEarn = round2(Number(input.proposedOwnerEarn));
  // Card portion === total − owner-direct portion (the proven identity).
  // The caller may override the *current* card basis with the actually-
  // captured amount (net captured) so the charged-now figure matches what
  // the server will move off the card.
  const currentPlatform = input.currentCardCapturedBasis != null
    ? round2(Number(input.currentCardCapturedBasis))
    : round2(currentTotal - currentOwnerEarn);
  const proposedPlatform = round2(proposedTotal - proposedOwnerEarn);

  return {
    isPartial: true,
    card: computeAdjustmentDelta(currentPlatform, proposedPlatform, "renter", currency, proposalStatus),
    ownerAtPickup: computeAdjustmentDelta(currentOwnerEarn, proposedOwnerEarn, "renter", currency, proposalStatus),
    newTotal: proposedTotal,
    currentTotal,
  };
}
